import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config/api';

const STATUS_COLORS = {
  submitted: 'bg-yellow-100 text-yellow-800',
  under_review: 'bg-blue-100 text-blue-800',
  accepted: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const HACKATHON_STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  upcoming: 'bg-blue-100 text-blue-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800',
};

const Hackathons = () => {
  const [hackathon, setHackathon] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const apiBase = API_BASE_URL;

  // ─── Fetch hackathon info ──────────────────────────────────────────────────
  const fetchHackathon = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${apiBase}/api/hackathons`, {
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setHackathon(data.data);
        await fetchSubmissions(data.data._id);
      } else {
        setError(data.message || 'Failed to fetch hackathon');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Failed to fetch hackathon');
    } finally {
      setLoading(false);
    }
  };

  // ─── Fetch all submissions for hackathon ──────────────────────────────────
  const fetchSubmissions = async (hackathonId) => {
    try {
      setLoadingSubmissions(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${apiBase}/api/hackathons/${hackathonId}/submissions`, {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (data.success) {
        setSubmissions(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching submissions:', err);
    } finally {
      setLoadingSubmissions(false);
    }
  };

  useEffect(() => {
    fetchHackathon();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Helpers ──────────────────────────────────────────────────────────────

  // Works for both Cloudinary absolute URLs and old local /uploads/... paths
  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `${apiBase}${url}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredSubmissions = submissions.filter((sub) => {
    const snap = sub.userSnapshot || {};
    const matchesSearch =
      !searchTerm ||
      snap.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      snap.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      snap.institution?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.selectedProblem?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || sub.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewDetail = (sub) => {
    setSelectedSubmission(sub);
    setShowModal(true);
  };

  const handleUpdateStatus = async (submissionId, newStatus) => {
    if (!hackathon) return;
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${apiBase}/api/hackathons/${hackathon._id}/submissions/${submissionId}/status`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify({ status: newStatus }),
        }
      );
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setSubmissions((prev) =>
            prev.map((s) => (s._id === submissionId ? { ...s, status: newStatus } : s))
          );
          if (selectedSubmission?._id === submissionId) {
            setSelectedSubmission((prev) => ({ ...prev, status: newStatus }));
          }
        }
      }
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // ─── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-red-800">Error loading hackathon</h3>
        <p className="mt-1 text-sm text-red-700">{error}</p>
        <button
          onClick={fetchHackathon}
          className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8">
      {/* ── Header ── */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-1">Hackathons</h1>
        <p className="text-gray-600">Manage hackathon submissions and participants</p>
      </div>

      {/* ── Hackathon Info Card ── */}
      {hackathon && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <h2 className="text-2xl font-bold text-gray-900">{hackathon.title}</h2>
                <span
                  className={`px-2 py-0.5 text-xs font-semibold rounded-full ${HACKATHON_STATUS_COLORS[hackathon.status] || 'bg-gray-100 text-gray-800'
                    }`}
                >
                  {hackathon.status}
                </span>
              </div>
              {hackathon.tagline && (
                <p className="text-gray-500 italic mb-2">{hackathon.tagline}</p>
              )}
              <p className="text-gray-700 text-sm max-w-2xl">{hackathon.description}</p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-blue-50 rounded-lg p-4 min-w-[100px]">
                <div className="text-3xl font-bold text-blue-700">{submissions.length}</div>
                <div className="text-xs text-blue-500 mt-1">Total Submissions</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 min-w-[100px]">
                <div className="text-3xl font-bold text-green-700">
                  {submissions.filter((s) => s.status === 'accepted').length}
                </div>
                <div className="text-xs text-green-500 mt-1">Accepted</div>
              </div>
            </div>
          </div>

          {/* Problem Statements */}
          {hackathon.problemStatements?.length > 0 && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm font-medium text-gray-700 mb-2">
                Problem Statements ({hackathon.problemStatements.length})
              </p>
              <div className="flex flex-wrap gap-2">
                {hackathon.problemStatements.map((ps) => (
                  <span
                    key={ps.id}
                    title={ps.title}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                  >
                    {ps.id}: {ps.title}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="mb-4 flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          placeholder="Search by name, email, institution, or problem..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
        >
          <option value="all">All Statuses</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
        </select>
        {(searchTerm || statusFilter !== 'all') && (
          <button
            onClick={() => { setSearchTerm(''); setStatusFilter('all'); }}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      {/* ── Submissions Table ── */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loadingSubmissions ? (
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <div className="text-center py-16">
            <div className="text-5xl mb-4">🏆</div>
            <p className="text-gray-500 text-lg">
              {submissions.length === 0
                ? 'No submissions yet for this hackathon.'
                : 'No submissions match your filters.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['#', 'Participant', 'Institution', 'Problem Statement', 'Submitted On', 'Status', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredSubmissions.map((sub, idx) => {
                  const snap = sub.userSnapshot || {};
                  return (
                    <tr key={sub._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {snap.profilePicture ? (
                            <img
                              src={snap.profilePicture}
                              alt={snap.name}
                              className="h-8 w-8 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm">
                              {snap.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {snap.name || sub.user?.name || 'N/A'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {snap.email || sub.user?.email || ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {snap.institution || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        <span className="font-medium text-blue-700">
                          {sub.selectedProblem?.id}
                        </span>{' '}
                        — {sub.selectedProblem?.title}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(sub.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[sub.status] || 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {sub.status || 'submitted'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">
                        <button
                          onClick={() => handleViewDetail(sub)}
                          className="text-blue-600 hover:text-blue-900 transition-colors"
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-600">
              Showing <span className="font-medium">{filteredSubmissions.length}</span> of{' '}
              <span className="font-medium">{submissions.length}</span> submissions
            </div>
          </div>
        )}
      </div>

      {/* ── Submission Detail Modal ── */}
      {showModal && selectedSubmission && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto z-50 flex items-start justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl my-8"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-xl font-bold text-gray-900">Submission Details</h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="px-6 py-5 space-y-6 max-h-[80vh] overflow-y-auto">
              {/* Participant Info */}
              <div>
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Participant Information
                </h4>
                <div className="flex items-center gap-4 mb-4">
                  {selectedSubmission.userSnapshot?.profilePicture ? (
                    <img
                      src={selectedSubmission.userSnapshot.profilePicture}
                      alt="profile"
                      className="h-16 w-16 rounded-full object-cover border"
                    />
                  ) : (
                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-2xl font-bold">
                      {selectedSubmission.userSnapshot?.name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div>
                    <p className="text-lg font-bold text-gray-900">
                      {selectedSubmission.userSnapshot?.name || 'N/A'}
                    </p>
                    <p className="text-sm text-gray-500">
                      @{selectedSubmission.userSnapshot?.username || 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  {[
                    { label: 'Email', value: selectedSubmission.userSnapshot?.email },
                    { label: 'Institution', value: selectedSubmission.userSnapshot?.institution },
                    { label: 'Degree', value: selectedSubmission.userSnapshot?.degree },
                    { label: 'Field of Study', value: selectedSubmission.userSnapshot?.fieldOfStudy },
                    { label: 'Occupation', value: selectedSubmission.userSnapshot?.occupation },
                    {
                      label: 'Location',
                      value: [
                        selectedSubmission.userSnapshot?.city,
                        selectedSubmission.userSnapshot?.state,
                        selectedSubmission.userSnapshot?.country,
                      ]
                        .filter(Boolean)
                        .join(', '),
                    },
                  ].map(({ label, value }) => (
                    <div key={label} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-500">{label}</p>
                      <p className="text-gray-800 mt-0.5">{value || 'N/A'}</p>
                    </div>
                  ))}
                </div>

                {/* Social Links */}
                {(selectedSubmission.userSnapshot?.github ||
                  selectedSubmission.userSnapshot?.linkedin ||
                  selectedSubmission.userSnapshot?.portfolio) && (
                    <div className="mt-3 flex flex-wrap gap-3">
                      {selectedSubmission.userSnapshot?.github && (
                        <a
                          href={selectedSubmission.userSnapshot.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-gray-800 text-white rounded-full hover:bg-gray-900 transition-colors"
                        >
                          GitHub
                        </a>
                      )}
                      {selectedSubmission.userSnapshot?.linkedin && (
                        <a
                          href={selectedSubmission.userSnapshot.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
                        >
                          LinkedIn
                        </a>
                      )}
                      {selectedSubmission.userSnapshot?.portfolio && (
                        <a
                          href={selectedSubmission.userSnapshot.portfolio}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs px-3 py-1.5 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
                        >
                          Portfolio
                        </a>
                      )}
                    </div>
                  )}
              </div>

              {/* Problem & Answer */}
              <div className="border-t pt-5">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Submission
                </h4>
                <div className="mb-3">
                  <p className="text-xs font-medium text-gray-500">Selected Problem</p>
                  <p className="text-gray-900 font-semibold mt-0.5">
                    <span className="text-blue-600">{selectedSubmission.selectedProblem?.id}</span>{' '}
                    — {selectedSubmission.selectedProblem?.title}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Answer / Proposal</p>
                  <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-800 whitespace-pre-wrap max-h-60 overflow-y-auto leading-relaxed">
                    {selectedSubmission.answer}
                  </div>
                </div>
              </div>

              {/* Uploaded Files */}
              {(selectedSubmission.videoUrl || selectedSubmission.imageUrl || (selectedSubmission.images && selectedSubmission.images.length > 0)) && (
                <div className="border-t pt-5">
                  <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Uploaded Files
                  </h4>
                  <div className="flex flex-wrap gap-4">
                    {selectedSubmission.images && selectedSubmission.images.length > 0 ? (
                      selectedSubmission.images.map((img, idx) => (
                        <div key={img._id || idx}>
                          <p className="text-xs text-gray-500 mb-1">
                            Image — {img.originalName || `image ${idx + 1}`}
                          </p>
                          <img
                            src={resolveUrl(img.url)}
                            alt={`submitted ${idx + 1}`}
                            className="max-h-48 rounded-lg border object-contain"
                          />
                        </div>
                      ))
                    ) : selectedSubmission.imageUrl ? (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          Image — {selectedSubmission.imageOriginalName || 'image'}
                        </p>
                        <img
                          src={resolveUrl(selectedSubmission.imageUrl)}
                          alt="submitted"
                          className="max-h-48 rounded-lg border object-contain"
                        />
                      </div>
                    ) : null}
                    {selectedSubmission.videoUrl && (
                      <div>
                        <p className="text-xs text-gray-500 mb-1">
                          Video — {selectedSubmission.videoOriginalName || 'video'}
                        </p>
                        <video
                          src={resolveUrl(selectedSubmission.videoUrl)}
                          controls
                          className="max-h-48 rounded-lg border"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t pt-5">
                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Update Status
                </h4>
                <div className="flex flex-wrap gap-2 items-center">
                  {['submitted', 'under_review', 'accepted', 'rejected'].map((s) => (
                    <button
                      key={s}
                      disabled={updatingStatus || selectedSubmission.status === s}
                      onClick={() => handleUpdateStatus(selectedSubmission._id, s)}
                      className={`px-4 py-2 text-sm rounded-full font-medium transition-all border ${selectedSubmission.status === s
                        ? `${STATUS_COLORS[s]} border-transparent cursor-default`
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-100'
                        } disabled:opacity-60`}
                    >
                      {s.replace('_', ' ')}
                      {selectedSubmission.status === s && ' ✓'}
                    </button>
                  ))}
                  {updatingStatus && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                  )}
                </div>
              </div>

              {/* Meta Info */}
              <div className="border-t pt-4 text-xs text-gray-400 flex gap-6">
                <span>Submitted: {formatDate(selectedSubmission.createdAt)}</span>
                <span>ID: {selectedSubmission._id}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Hackathons;
